import React, { useEffect, useRef, useState } from 'react'
import { Collapse, Popover } from 'antd'
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import { Link, useHistory } from 'react-router-dom'
import moment from 'moment'
import {
  FILTER_KEYS,
  getHealthPanelSummaryView,
} from 'src/utils/reconciliationHealth'
import {
  RECEIPTING_HEALTH_BADGE_COLOR,
  shouldShowReceiptingHealthBadge,
  buildInvoicesPath,
  buildReceiptingPath,
  buildCreditRequestsPath,
  shouldShowCreditRequestHealthBadge,
  resolveHealthPanelCreditRequestCount,
} from 'src/utils/reconciliationHealthApi'
import {
  resolveInitialHealthPanelOpen,
  setStoredHealthPanelOpen,
} from 'src/utils/reconciliationHealthStorage'

const { Panel: CollapsePanel } = Collapse
const HEALTH_PANEL_KEY = 'health'

const TERMINOLOGY_ITEMS = [
  {
    term: 'Receipted',
    meaning:
      'Delivery saved on Goods receipting (Accept / Save / Full accept). Does not by itself mean the invoice is reconciled.',
  },
  {
    term: 'Reconciled',
    meaning:
      'This PO has at least one linked invoice with lifecycle Reconciled or Sent to Xero (Match & reconcile finished). Separate from Receipted.',
  },
  {
    term: 'No invoice attached',
    meaning:
      'This PO has no invoice file yet — same idea as Received on Invoices & Credit Notes.',
  },
  {
    term: 'Invoice attached',
    meaning:
      'An invoice is linked to this PO but may still need Match & reconcile. Not the same as reconciled.',
  },
  {
    term: 'Sent to Xero',
    meaning:
      'Synced to your accounting platform — counts as reconciled for this PO.',
  },
  {
    term: 'Credit requests',
    meaning:
      'Open Ask-for-credit lines on Credit Requests (same count as the amber badge). Not the same as every PO in a credit receipting status.',
  },
  {
    term: 'Awaiting supplier',
    meaning:
      'The order is not yet confirmed by the supplier — receipting may be blocked.',
  },
]

const CHIP_CONFIG = [
  {
    key: FILTER_KEYS.NEEDS_RECEIPTING,
    label: 'Needs receipting',
    countKey: 'needsReceipting',
    tone: 'attention',
  },
  {
    key: FILTER_KEYS.NEEDS_RECONCILING,
    label: 'Needs reconciling',
    countKey: 'needsReconciling',
    tone: 'attention',
  },
  {
    key: FILTER_KEYS.NO_INVOICE,
    label: 'No invoice attached',
    countKey: 'noInvoice',
    tone: 'neutral',
  },
  {
    key: FILTER_KEYS.NOT_SAVED,
    label: 'Not saved',
    countKey: 'notSaved',
    tone: 'neutral',
  },
  {
    key: FILTER_KEYS.AWAITING_SUPPLIER,
    label: 'Awaiting supplier',
    countKey: 'awaitingSupplier',
    tone: 'neutral',
  },
  {
    key: FILTER_KEYS.CREDIT_WORKFLOW,
    label: 'Credit requests',
    // Display count comes from openCreditRequestCount (credits-history),
    // not healthSummary.creditWorkflow (PO receiptingStatus).
    countKey: 'openCreditRequestCount',
    tone: 'attention',
    opensCreditRequests: true,
  },
  {
    key: FILTER_KEYS.COMPLETE,
    label: 'Receipted',
    countKey: 'receipted',
    tone: 'success',
  },
  {
    key: FILTER_KEYS.RECONCILED,
    label: 'Reconciled',
    countKey: 'invoiceReconciled',
    tone: 'success',
  },
  {
    key: FILTER_KEYS.VOIDED,
    label: 'Voided',
    countKey: 'voided',
    tone: 'neutral',
  },
]

const TerminologyList = styled.ul`
  margin: 0;
  padding-left: 18px;
  max-width: 320px;

  li {
    margin-bottom: 8px;
    font-size: 13px;
    line-height: 1.45;
    color: #595959;
  }

  li:last-child {
    margin-bottom: 0;
  }
`

const terminologyContent = (
  <TerminologyList>
    {TERMINOLOGY_ITEMS.map(item => (
      <li key={item.term}>
        <strong>{item.term}</strong> — {item.meaning}
      </li>
    ))}
  </TerminologyList>
)

const ReconciliationHealthPanel = ({
  venueId,
  healthSummary,
  filterDates,
  activeFilter,
  onFilterChange,
  orphanInvoiceCount = 0,
  readyToReconcileCount = 0,
  openCreditRequestCount = 0,
  invoiceHealth = null,
  invoiceHealthItems = [],
  ordersLoaded = false,
  autoExpandSignal = null,
}) => {
  const history = useHistory()
  const [panelOpen, setPanelOpen] = useState(null)
  const initializedVenueRef = useRef(null)
  const lastAutoExpandKeyRef = useRef(null)

  const view = getHealthPanelSummaryView(healthSummary || {})
  const {
    total,
    percentReceipted,
    needsReceipting,
    needsReconciling,
    isFullyReceipted,
    hasOutstandingReconciling,
    headlineCounts,
    progressLabel,
    educationLine,
    successBanner,
    receiptedNoInvoiceCallout,
  } = view

  const creditRequestChipCount = resolveHealthPanelCreditRequestCount({
    openCreditRequestCount,
  })

  // Badge still uses receipting outstanding (legacy needsAction === needsReceipting).
  const needsAction = needsReceipting

  useEffect(() => {
    if (!ordersLoaded || !venueId) {
      setPanelOpen(null)
      initializedVenueRef.current = null
      lastAutoExpandKeyRef.current = null
      return
    }

    if (initializedVenueRef.current === venueId) return

    initializedVenueRef.current = venueId
    setPanelOpen(resolveInitialHealthPanelOpen(venueId, { needsAction, total }))
  }, [venueId, ordersLoaded, needsAction, total])

  useEffect(() => {
    if (!ordersLoaded || !venueId || !autoExpandSignal) return

    const autoExpandKey = `${venueId}:${autoExpandSignal}`
    if (lastAutoExpandKeyRef.current === autoExpandKey) return

    lastAutoExpandKeyRef.current = autoExpandKey
    setPanelOpen(true)
    setStoredHealthPanelOpen(venueId, true)
  }, [venueId, ordersLoaded, autoExpandSignal])

  if (!ordersLoaded) return null

  const dateRangeLabel = `${moment(filterDates?.start).format(
    'DD/MM/YYYY',
  )} – ${moment(filterDates?.end).format('DD/MM/YYYY')}`

  const collapseSubtitle =
    total > 0
      ? `${headlineCounts} · ${percentReceipted}% receipted · ${dateRangeLabel}`
      : `No orders in this period · ${dateRangeLabel}`

  const handleChipClick = filterKey => {
    const chip = CHIP_CONFIG.find(item => item.key === filterKey)
    if (chip?.opensCreditRequests && venueId) {
      history.push(buildCreditRequestsPath(venueId))
      return
    }
    if (activeFilter === filterKey) {
      onFilterChange(FILTER_KEYS.ALL)
      return
    }
    onFilterChange(filterKey)
  }

  const handlePanelChange = keys => {
    const isOpen = keys.includes(HEALTH_PANEL_KEY)
    setPanelOpen(isOpen)
    setStoredHealthPanelOpen(venueId, isOpen)
  }

  const collapseHeader = (
    <CollapseHeader>
      <CollapseHeaderMain>
        <PanelTitle>
          Reconciliation health
          {shouldShowReceiptingHealthBadge(needsAction) ? (
            <VisuallyHidden>{`, ${needsAction}`}</VisuallyHidden>
          ) : null}
        </PanelTitle>
        {shouldShowReceiptingHealthBadge(needsAction) && (
          // Static count only — ant Badge ScrollNumber exposes 0–9 mid-animation to AT.
          <HealthCountBadge
            aria-hidden='true'
            data-testid='reconciliation-health-count'
          >
            {needsAction}
          </HealthCountBadge>
        )}
        {isFullyReceipted && !hasOutstandingReconciling && (
          <ReconciledIcon aria-label='All orders receipted and reconciled'>
            <CheckCircleOutlined />
          </ReconciledIcon>
        )}
      </CollapseHeaderMain>
      <CollapseSubtitle>{collapseSubtitle}</CollapseSubtitle>
    </CollapseHeader>
  )

  return (
    <Panel>
      <StyledCollapse
        bordered={false}
        activeKey={panelOpen ? [HEALTH_PANEL_KEY] : []}
        onChange={handlePanelChange}
      >
        <CollapsePanel header={collapseHeader} key={HEALTH_PANEL_KEY}>
          <ExpandedContent>
            <PanelHeader>
              <HeadingRow>
                <DateHint>Based on orders from {dateRangeLabel}</DateHint>
                <Popover
                  content={terminologyContent}
                  title='Terminology'
                  trigger='click'
                  placement='bottomLeft'
                >
                  <TerminologyButton
                    type='button'
                    aria-label='Reconciliation terminology'
                  >
                    <InfoCircleOutlined />
                    <span>Terminology</span>
                  </TerminologyButton>
                </Popover>
              </HeadingRow>
            </PanelHeader>

            {total > 0 ? (
              <>
                <SummaryRow>
                  <ProgressTrack
                    $complete={isFullyReceipted}
                    role='progressbar'
                    aria-valuenow={percentReceipted}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label='Percent of orders receipted'
                  >
                    <ProgressFill
                      $percent={percentReceipted}
                      $complete={isFullyReceipted}
                    />
                  </ProgressTrack>
                  <SummaryText>
                    <SummaryPrimary>
                      <HeadlineCounts>{headlineCounts}</HeadlineCounts>
                    </SummaryPrimary>
                    <SummarySecondary>
                      <ClickableSummary
                        type='button'
                        onClick={() => onFilterChange(FILTER_KEYS.COMPLETE)}
                      >
                        {progressLabel}
                      </ClickableSummary>
                    </SummarySecondary>
                    {(needsReceipting > 0 || needsReconciling > 0) && (
                      <SummarySecondary>
                        {needsReceipting > 0 ? (
                          <>
                            <strong>{needsReceipting}</strong> need receipting
                            {' · '}
                            <InlineAction
                              type='button'
                              onClick={() =>
                                onFilterChange(FILTER_KEYS.NEEDS_RECEIPTING)
                              }
                            >
                              Show needs receipting
                            </InlineAction>
                          </>
                        ) : null}
                        {needsReceipting > 0 && needsReconciling > 0
                          ? ' · '
                          : null}
                        {needsReconciling > 0 ? (
                          <>
                            <strong>{needsReconciling}</strong> need reconciling
                            {' · '}
                            <InlineAction
                              type='button'
                              onClick={() =>
                                onFilterChange(FILTER_KEYS.NEEDS_RECONCILING)
                              }
                            >
                              Show needs reconciling
                            </InlineAction>
                          </>
                        ) : null}
                      </SummarySecondary>
                    )}
                  </SummaryText>
                </SummaryRow>

                {receiptedNoInvoiceCallout ? (
                  <ReceiptedGapCallout>
                    <InfoCircleOutlined />
                    <span>
                      {receiptedNoInvoiceCallout.message}{' '}
                      <InlineAction
                        type='button'
                        onClick={() =>
                          onFilterChange(FILTER_KEYS.NEEDS_RECONCILING)
                        }
                      >
                        Show needs reconciling
                      </InlineAction>
                    </span>
                  </ReceiptedGapCallout>
                ) : null}

                <EducationLine>{educationLine}</EducationLine>
              </>
            ) : (
              <EducationLine>
                No orders in this date range. Try widening your dates to review
                reconciliation health.
              </EducationLine>
            )}

            {successBanner ? (
              <SuccessBanner
                $warning={successBanner.claimsInvoicedAmounts === false}
              >
                <CheckCircleOutlined />
                <span>
                  <strong>{successBanner.title}</strong> {successBanner.detail}
                </span>
              </SuccessBanner>
            ) : null}

            {readyToReconcileCount > 0 && (
              <ReadyToReconcileCallout>
                <CheckCircleOutlined />
                <span>
                  <strong>{readyToReconcileCount}</strong> invoice
                  {readyToReconcileCount === 1 ? '' : 's'} ready to reconcile
                  with one click (Match Found). Review the totals and confirm on
                  Invoices &amp; Credit Notes.{' '}
                  <Link
                    to={buildInvoicesPath(venueId, {
                      matching: 'match_found',
                    })}
                  >
                    Go to Match Found
                  </Link>
                </span>
              </ReadyToReconcileCallout>
            )}

            {shouldShowCreditRequestHealthBadge(openCreditRequestCount) ? (
              <CreditRequestCallout>
                <InfoCircleOutlined />
                <span>
                  <strong>{openCreditRequestCount}</strong> open credit request
                  {openCreditRequestCount === 1 ? '' : 's'} — chase the
                  supplier, upload a Credit Note, or mark resolved when done.{' '}
                  <Link to={buildCreditRequestsPath(venueId)}>
                    Open Credit Requests
                  </Link>
                </span>
              </CreditRequestCallout>
            ) : null}

            {(invoiceHealth?.unmatched > 0 || orphanInvoiceCount > 0) && (
              <OrphanCallout>
                <InfoCircleOutlined />
                <span>
                  You have{' '}
                  <strong>
                    {invoiceHealth?.unmatched ?? orphanInvoiceCount}
                  </strong>{' '}
                  unmatched invoice
                  {(invoiceHealth?.unmatched ?? orphanInvoiceCount) === 1
                    ? ''
                    : 's'}{' '}
                  with no OMS order yet. Link each one to a PO, allocate across
                  multiple POs, or delete and re-upload if it is a duplicate or
                  unreadable.{' '}
                  <Link
                    to={buildInvoicesPath(venueId, {
                      matching: ['no_match', 'unprocessed'],
                    })}
                  >
                    Review unmatched invoices
                  </Link>
                </span>
              </OrphanCallout>
            )}

            {invoiceHealth?.possibleDuplicates > 0 && (
              <DuplicateCallout>
                <InfoCircleOutlined />
                <span>
                  <strong>{invoiceHealth.possibleDuplicates}</strong> possible
                  duplicate invoice
                  {invoiceHealth.possibleDuplicates === 1 ? '' : 's'} need a
                  quick review before you attach or reconcile.{' '}
                  <Link
                    to={buildInvoicesPath(venueId, {
                      matching: 'possible_duplicate',
                    })}
                  >
                    Review duplicates
                  </Link>
                </span>
              </DuplicateCallout>
            )}

            {invoiceHealth?.agingUnmatched > 0 && (
              <AgingCallout>
                <InfoCircleOutlined />
                <span>
                  <strong>{invoiceHealth.agingUnmatched}</strong> unmatched
                  invoice
                  {invoiceHealth.agingUnmatched === 1 ? '' : 's'}{' '}
                  {invoiceHealth.agingUnmatched === 1 ? 'is' : 'are'} older than{' '}
                  {invoiceHealth.agingDays || 7} days — prioritise these so
                  Spend over time stays accurate.{' '}
                  <Link
                    to={buildInvoicesPath(venueId, {
                      matching: ['no_match', 'unprocessed'],
                    })}
                  >
                    Review aging invoices
                  </Link>
                </span>
              </AgingCallout>
            )}

            {invoiceHealthItems.length > 0 && (
              <WorkQueue>
                <WorkQueueTitle>What do I need to do?</WorkQueueTitle>
                <WorkQueueList>
                  {invoiceHealthItems.slice(0, 8).map(item => (
                    <WorkQueueItem key={`${item.bucket}-${item.id}`}>
                      <div>
                        <WorkQueuePrimary>
                          {item.invoiceNumber || 'Untitled invoice'}
                          {item.supplierTitle ? ` · ${item.supplierTitle}` : ''}
                        </WorkQueuePrimary>
                        <WorkQueueSecondary>
                          {item.recommendedAction ||
                            item.matchingStatus.replace(/_/g, ' ')}
                          {item.ageDays != null
                            ? ` · ${item.ageDays} day${
                                item.ageDays === 1 ? '' : 's'
                              } old`
                            : ''}
                        </WorkQueueSecondary>
                      </div>
                      <Link
                        to={buildInvoicesPath(venueId, {
                          invoiceId: item.deepLink?.invoiceId || item.id,
                        })}
                      >
                        Open
                      </Link>
                    </WorkQueueItem>
                  ))}
                </WorkQueueList>
                {healthSummary?.noInvoice > 0 && (
                  <WorkQueueFooter>
                    Also {healthSummary.noInvoice} order
                    {healthSummary.noInvoice === 1 ? '' : 's'} still need an
                    invoice attached —{' '}
                    <Link
                      to={buildReceiptingPath(venueId, FILTER_KEYS.NO_INVOICE)}
                    >
                      show orders with no invoice
                    </Link>
                    .
                  </WorkQueueFooter>
                )}
              </WorkQueue>
            )}
          </ExpandedContent>
        </CollapsePanel>
      </StyledCollapse>

      {total > 0 && (
        <ChipRow>
          {CHIP_CONFIG.map(chip => {
            const count =
              chip.countKey === 'openCreditRequestCount'
                ? creditRequestChipCount
                : healthSummary?.[chip.countKey] ?? 0
            const isActive = activeFilter === chip.key
            return (
              <FilterChip
                key={chip.key}
                type='button'
                $tone={chip.tone}
                $active={isActive}
                $zero={count === 0}
                onClick={() => handleChipClick(chip.key)}
                title={
                  chip.opensCreditRequests
                    ? 'Open Credit Requests (Ask for credit)'
                    : undefined
                }
              >
                {count} {chip.label}
              </FilterChip>
            )
          })}
          {activeFilter !== FILTER_KEYS.ALL && (
            <ClearFilterButton
              type='button'
              onClick={() => onFilterChange(FILTER_KEYS.ALL)}
            >
              Clear filters
            </ClearFilterButton>
          )}
        </ChipRow>
      )}
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 4px;
`

const StyledCollapse = styled(Collapse)`
  background: transparent;

  .ant-collapse-item {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    overflow: hidden;
    background: #fafafa;
  }

  .ant-collapse-header {
    align-items: center !important;
    padding: 12px 16px 12px 36px !important;
  }

  .ant-collapse-arrow {
    left: 14px !important;
  }

  .ant-collapse-content-box {
    padding: 0 16px 16px !important;
  }
`

const CollapseHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-right: 8px;
`

const CollapseHeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const CollapseSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.4;
`

const ReconciledIcon = styled.span`
  color: #52c41a;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
`

const ExpandedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const HealthCountBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background-color: ${RECEIPTING_HEALTH_BADGE_COLOR};
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  font-weight: 500;
`

const TerminologyButton = styled.button`
  border: none;
  background: transparent;
  color: #8c8c8c;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;

  &:hover {
    color: #595959;
  }
`

const DateHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #8c8c8c;
`

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`

const ProgressTrack = styled.div`
  width: 120px;
  height: 10px;
  border-radius: 999px;
  background: ${props => (props.$complete ? '#d9f7be' : '#fff1b8')};
  overflow: hidden;
  flex-shrink: 0;
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => (props.$complete ? '#52c41a' : '#faad14')};
  border-radius: 999px;
  transition: width 0.2s ease;
`

const SummaryText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const SummaryPrimary = styled.p`
  margin: 0;
  font-size: 15px;
  color: #262626;
`

const HeadlineCounts = styled.span`
  font-weight: 600;
  color: #262626;
`

const SummarySecondary = styled.p`
  margin: 0;
  font-size: 14px;
  color: #595959;
`

const ClickableSummary = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: #389e0d;
    text-decoration: underline;
  }
`

const InlineAction = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  color: #389e0d;
  cursor: pointer;
  text-decoration: underline;
`

const EducationLine = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #8c8c8c;
`

const ReceiptedGapCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
  font-size: 13px;
  line-height: 1.45;
  color: #874d00;

  .anticon {
    margin-top: 2px;
    color: #faad14;
  }

  span {
    flex: 1;
  }
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`

const FilterChip = styled.button`
  border: 1px solid
    ${props =>
      props.$active
        ? props.$tone === 'success'
          ? '#52c41a'
          : props.$tone === 'attention'
          ? '#fa8c16'
          : '#8c8c8c'
        : '#d9d9d9'};
  background: ${props =>
    props.$active
      ? props.$tone === 'success'
        ? '#f6ffed'
        : props.$tone === 'attention'
        ? '#fff7e6'
        : '#fafafa'
      : '#ffffff'};
  color: ${props => (props.$zero && !props.$active ? '#bfbfbf' : '#434343')};
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${props =>
      props.$tone === 'success'
        ? '#52c41a'
        : props.$tone === 'attention'
        ? '#fa8c16'
        : '#8c8c8c'};
  }
`

const ClearFilterButton = styled.button`
  border: none;
  background: transparent;
  color: #8c8c8c;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
`

const BannerBase = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
`

const SuccessBanner = styled(BannerBase)`
  background-color: ${props => (props.$warning ? '#fffbe6' : '#f6ffed')};
  color: ${props => (props.$warning ? '#874d00' : '#135200')};
  border: 1px solid ${props => (props.$warning ? '#ffe58f' : '#b7eb8f')};

  .anticon {
    margin-top: 2px;
    color: ${props => (props.$warning ? '#faad14' : '#52c41a')};
  }

  span {
    display: flex;
    gap: 8px;
  }
`

const OrphanCallout = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #003a8c;

  a {
    color: #096dd9;
    font-weight: 500;
    white-space: nowrap;
  }
`

const DuplicateCallout = styled(OrphanCallout)`
  background: #f9f0ff;
  border-color: #d3adf7;
  color: #391085;

  a {
    color: #531dab;
  }
`

const AgingCallout = styled(OrphanCallout)`
  background: #fff7e6;
  border-color: #ffd591;
  color: #873800;

  a {
    color: #d46b08;
  }
`

const WorkQueue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 12px;
`

const WorkQueueTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
`

const WorkQueueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const WorkQueueItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid #f5f5f5;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }

  a {
    flex-shrink: 0;
    font-weight: 500;
    color: #389e0d;
  }
`

const WorkQueuePrimary = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
`

const WorkQueueSecondary = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.4;
`

const WorkQueueFooter = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #595959;

  a {
    color: #389e0d;
    font-weight: 500;
  }
`

const ReadyToReconcileCallout = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #135200;

  .anticon {
    margin-top: 2px;
    color: #52c41a;
  }

  a {
    color: #389e0d;
    font-weight: 500;
    white-space: nowrap;
  }
`

const CreditRequestCallout = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #ad6800;

  .anticon {
    margin-top: 2px;
    color: #fa8c16;
  }

  a {
    color: #d46b08;
    font-weight: 500;
    white-space: nowrap;
  }
`

export default ReconciliationHealthPanel
